import React, { Fragment, Component } from 'react'
import { shape, string, func, bool, number, oneOfType } from 'prop-types'
import { createOrGetVideo } from '@ycos/brightcove'
import { createComponent } from '@ycos/fela'
import { Caption } from '@ycos/primitives'
import { hasTag } from '@ycos/coremedia-renderer'
import { fullBleedStyles } from '@ycos/layout-fullbleed'
import LayoutWithMargins from '@ycos/layout-with-margins'

const getPaddingFromAspectRatio = (aspectRatio) => {
  const [height, width] = aspectRatio.split(':')
  return (width / height) * 100
}

const VideoContainer = createComponent('VideoContainer', ({ isFullBleed, aspectRatio, theme }) => {
  if (isFullBleed) {
    const videoWidthWithoutMargins = theme.breakpoints.xlarge.width - 2 * theme.breakpoints.xlarge.marginOuter

    return {
      ...fullBleedStyles,

      'screen-xlarge': {
        width: `${videoWidthWithoutMargins}px`,
        /**
         * We push the container to the middle of the page with left: 50% and pull it to the left corner with marginleft: -50vw
         * After that we calculate the value of the margins with (100vw - videoWidthWithoutMargins) and divide it by 2 to have equal margins on the right side and left
         */
        left: `calc(50% + ((100vw - ${videoWidthWithoutMargins}px)/2))`
      }
    }
  }

  return {
    maxWidth: '100%',
    display: 'block',
    position: 'relative',
    paddingTop: `${getPaddingFromAspectRatio(aspectRatio)}%`
  }
})

const VideoCaption = createComponent(
  'VideoCaption',
  ({ theme }) => ({
    marginTop: `${theme.spacingMultiplier}px`,
    textAlign: 'left'
  }),
  Caption
)

class StandardVideo extends Component {
  constructor(props) {
    super(props)
    this.videoNode = React.createRef()
    this.state = {
      videoError: false,
      isVideoOver95Percent: false
    }
  }

  componentDidMount() {
    const { config, brightcove, item, soundless } = this.props
    const { account, player } = (config && config.brightcove) || brightcove
    // Backwards compatibility
    let playerID = (soundless ? player.soundless : player.regular) || player
    const is360Video = hasTag(item.tags, 'Video.Spherical')
    if (is360Video) {
      playerID = 'SyOcUD16M'
    }
    createOrGetVideo(account, playerID, this.videoNode.current).then((videoPlayerInstance) => {
      // Checking if the video player has loaded with out any errors
      if (videoPlayerInstance.error_) {
        this.setState({
          videoError: true
        })
      }

      if (videoPlayerInstance) {
        this.props.onVideoPlay && videoPlayerInstance.on('play', this.props.onVideoPlay)
        this.props.onVideoEnded && videoPlayerInstance.on('ended', this.props.onVideoEnded)
        this.props.onVideoTimeUpdate &&
          videoPlayerInstance.on('timeupdate', (event) => {
            const isVideoOver95Percent = this.props.onVideoTimeUpdate(event, this.state.isVideoOver95Percent)
            if (isVideoOver95Percent !== this.state.isVideoOver95Percent) {
              this.setState({
                isVideoOver95Percent
              })
            }
          })
      }
    })
  }

  shouldComponentUpdate(nextProps) {
    if (this.props.visible !== undefined && nextProps.visible !== undefined && this.props.visible !== nextProps.visible) {
      this.stopVideo()
    }
    return true
  }

  stopVideo = () => {
    const localNode = this.videoNode.current
    localNode.pause()
  }

  static layoutVariant = 'isc-video'

  render() {
    const { aspectRatio, item, config, brightcove, soundless, loop } = this.props
    const { videoError } = this.state
    const { dataURL, detailTextPlain, ynapParameter, brightCoveDataUrl, tags, dataCmMetadata } = item
    const { account, player } = (config && config.brightcove) || brightcove
    // Backwards compatibility
    let playerID = (soundless ? player.soundless : player.regular) || player

    // Backwards compatibility for watch hub
    const videoId = brightCoveDataUrl || ynapParameter || dataURL

    const isFullBleed = hasTag(tags, 'Video.FullBleed')
    const is360Video = hasTag(tags, 'Video.Spherical')
    if (is360Video) {
      playerID = 'SyOcUD16M'
    }

    const videoStyle = {
      position: !isFullBleed ? 'absolute' : 'relative',
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      width: '100%',
      height: '100%',
      background: videoError ? '#000' : 'transparent' // Sets transparency to fix subpixels on video resize
    }

    const commonProps = {
      ref: this.videoNode,
      'data-video-id': videoId,
      'data-account': account,
      'data-player': playerID,
      'data-embed': 'default',
      'data-application-id': '',
      'data-playlist-id': '',
      style: videoStyle,
      loop
    }

    return (
      <Fragment>
        <VideoContainer isFullBleed={isFullBleed} aspectRatio={aspectRatio} data-cm-metadata={dataCmMetadata}>
          {is360Video
            ? <video-js {...commonProps} className="vjs-fluid" controls="" />
            : <video {...commonProps} className="video-js" />
          }
          {isFullBleed && detailTextPlain && (
            <LayoutWithMargins>
              <VideoCaption>{detailTextPlain}</VideoCaption>
            </LayoutWithMargins>
          )}
        </VideoContainer>
        {!isFullBleed && detailTextPlain && <VideoCaption>{detailTextPlain}</VideoCaption>}
      </Fragment>
    )
  }
}

StandardVideo.defaultProps = {
  visible: false,
  soundless: false,
  loop: false,
  brightcove: {
    account: '88951765001',
    player: {
      regular: 'n0a1nFV70',
      soundless: 'HkVWbtmsM'
    }
  },
  aspectRatio: '16:9',
  onVideoTimeUpdate: () => {}
}

StandardVideo.propTypes = {
  visible: bool,
  soundless: bool,
  loop: bool,
  brightcove: shape({
    account: string,
    player: oneOfType([
      shape({
        regular: string,
        soundless: string
      }),
      string
    ])
  }),
  aspectRatio: string,
  onVideoPlay: func,
  onVideoEnded: func,
  config: shape({
    brightcove: shape({
      account: number,
      player: shape({
        regular: string,
        soundless: string
      })
    })
  }),
  onVideoTimeUpdate: func
}

export default StandardVideo
